/**
 * Shared wishlist state. Fetches the current customer's wishlist once per
 * page load and lets any number of <wishlist-button> instances read it
 * without each issuing their own network request.
 *
 * Fails silently (empty set, no throw) if the backend app isn't installed
 * or unreachable, so callers never need their own try/catch.
 */
const WISHLIST_ENDPOINT = '/apps/wishlist';

class WishlistStore extends EventTarget {
  /** @type {Set<string> | null} */
  #variantIds = null;
  /** @type {Promise<Set<string>> | null} */
  #pending = null;
  /** True once a request has succeeded or failed; tells callers whether the backend is reachable. */
  available = true;

  async load() {
    if (this.#variantIds) return this.#variantIds;
    if (this.#pending) return this.#pending;

    this.#pending = this.#fetchWishlist();
    this.#variantIds = await this.#pending;
    this.#pending = null;
    return this.#variantIds;
  }

  async #fetchWishlist() {
    try {
      const response = await fetch(WISHLIST_ENDPOINT, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        this.available = false;
        return new Set();
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        this.available = false;
        return new Set();
      }

      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      return new Set(items.map((item) => String(item.variant_id)));
    } catch {
      this.available = false;
      return new Set();
    }
  }

  has(variantId) {
    return Boolean(this.#variantIds?.has(String(variantId)));
  }

  add(variantId) {
    this.#variantIds?.add(String(variantId));
    this.dispatchEvent(new CustomEvent('change'));
  }

  remove(variantId) {
    this.#variantIds?.delete(String(variantId));
    this.dispatchEvent(new CustomEvent('change'));
  }
}

window.wishlistStore ||= new WishlistStore();
